import { getPokerMin } from '@/components/images/base64/PokerMin';
import { getPattern, updatePattern } from '@/utils/games/teenpatti';
import { Collapse, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GameResultCellProps } from '..';

const getOpacity = (index: number, sixCardLightArr: Array<number>) => {
    return sixCardLightArr.indexOf(index) >= 0 ? 1 : 0.4;
};

const SixPoker = (
    card: number,
    idx: number,
    sixCardLightArr: Array<number>
) => {
    return (
        <Grid
            item
            key={`six-poker-${idx}`}
            className="card-container"
            sx={{
                opacity: getOpacity(idx, sixCardLightArr),
            }}
        >
            {getPokerMin(card)};
        </Grid>
    );
};

const SixCardBonus = (props: ResultDisplay) => {
    const { sixCardPokers, sixCardLightArr, sixCardPattern } = props;

    const { t } = useTranslation();

    return (
        <Grid container className="six-card-bonus-container">
            <Grid container className="six-card-bonus-container-content">
                <Grid item className="six-card-bonus-container-content-title">
                    {t('teenpatti.sixCardBonus')}
                </Grid>
                <Grid
                    container
                    className="six-card-bonus-container-content-cards"
                >
                    {sixCardPokers.map((card, index) => (
                        <>{SixPoker(card, index, sixCardLightArr)}</>
                    ))}
                </Grid>
                <Grid className="six-card-bonus-container-content-cards-title">
                    {t('teenpatti.sixCardPattern.' + sixCardPattern)}
                </Grid>
            </Grid>
        </Grid>
    );
};

type PlayerCardsProps = {
    isPlayerA: boolean;
    cards: Array<number>;
    resultView: ResultDisplay | undefined;
    winSlot: number;
};

const PlayerCards = (props: PlayerCardsProps) => {
    const { isPlayerA = true, cards, resultView, winSlot } = props;
    const { t } = useTranslation();

    const getOpacity = (index: number) => {
        if (!resultView) return 1;
        return (isPlayerA
            ? resultView.playerALightArr
            : resultView.playerBLightArr
        ).indexOf(index) >= 0
            ? 1
            : 0.4;
    };

    const PlayerPoker = (card: number, idx: number) => {
        return (
            <Grid
                item
                key={`player-poker-${idx}`}
                className="card-container"
                sx={{
                    opacity: getOpacity(idx),
                }}
            >
                {getPokerMin(card)}
            </Grid>
        );
    };

    const getCardPatternTxt = () => {
        if (!resultView) return '';
        return t(
            'teenpatti.playerPattern.' +
                (isPlayerA
                    ? resultView.playerAPattern
                    : resultView.playerBPattern)
        );
    };

    const getBgColor = () => {
        if (!resultView) return 'black';
        if (isPlayerA && resultView.winResult == 1) return '#436FFF';
        if (!isPlayerA && resultView.winResult == 2) return '#D30000';
        return 'black';
    };

    const getBorderColor = () => {
        if (!resultView) return '#655840';
        if (isPlayerA && resultView.winResult == 1) return '#436FFF';
        if (!isPlayerA && resultView.winResult == 2) return '#D30000';
        return '#655840';
    };

    return (
        <Grid container className="player-cards-container">
            <Grid
                className="player-cards-container-title"
                color={isPlayerA ? '#436fff' : '#d30000'}
            >
                {`${isPlayerA ? 'A' : 'B'}`}
            </Grid>
            <Grid container className="player-cards-container-cards">
                {cards.map((card, index) => (
                    <>{PlayerPoker(card, index)}</>
                ))}
            </Grid>
            {resultView && (
                <Grid
                    className="player-cards-container-cards-title"
                    visibility={winSlot == 0 ? 'hidden' : 'visible'}
                    bgcolor={getBgColor()}
                    border={`2px solid ${getBorderColor()}`}
                >
                    {getCardPatternTxt()}
                </Grid>
            )}
        </Grid>
    );
};

type ResultDisplay = {
    winResult: number;
    playerAPattern: number;
    playerBPattern: number;
    sixCardPattern: number;
    sixCardPokers: Array<number>;
    playerALightArr: Array<number>;
    playerBLightArr: Array<number>;
    sixCardLightArr: Array<number>;
};

export const TeenPattiGameResult = (props: GameResultCellProps) => {
    const { summary, expanded } = props;

    const [resultView, setResultView] = useState<ResultDisplay>();
    const resultPlayer = useRef<Array<number>>([]);
    const resultBanker = useRef<Array<number>>([]);

    useEffect(() => {
        resultPlayer.current = [];
        resultBanker.current = [];

        const {
            winResult,
            playerAPattern,
            playerBPattern,
            sixCardPattern,
            sixCardPokers,
        } = getPattern(BigInt(summary.WinSlot));

        let tokens: Array<string> = summary.Result.split(',');
        let i: number;
        for (i = 1; i < 1 + parseInt(tokens[0]); i++) {
            resultPlayer.current.push(parseInt(tokens[i]));
        }
        for (i = 2 + resultPlayer.current.length; i < tokens.length; i++) {
            resultBanker.current.push(parseInt(tokens[i]));
        }

        setResultView({
            winResult,
            playerAPattern,
            playerBPattern,
            sixCardPattern,
            sixCardPokers,
            playerALightArr: updatePattern(
                resultPlayer.current,
                playerAPattern,
                false
            ),
            playerBLightArr: updatePattern(
                resultBanker.current,
                playerBPattern,
                false
            ),
            sixCardLightArr: updatePattern(sixCardPokers, sixCardPattern, true),
        });
    }, [summary, expanded, resultBanker]);

    return (
        <Box className="item-teenpatti-game-result-container">
            <Box className="teenpatti-game-result-container-box">
                <Box className="teenpatti-game-result-player-container">
                    <PlayerCards
                        isPlayerA={true}
                        cards={resultPlayer.current}
                        resultView={resultView}
                        winSlot={summary.WinSlot}
                    />
                    <PlayerCards
                        isPlayerA={false}
                        cards={resultBanker.current}
                        resultView={resultView}
                        winSlot={summary.WinSlot}
                    />
                </Box>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {resultView && <SixCardBonus {...resultView} />}
                </Collapse>
            </Box>
        </Box>
    );
};
